// Core content i18n — only the helpers Landing.jsx + i18n.js need eagerly.
//
// v60.16.25 — PERF: This file used to be a 69-KB monolith holding every
// content-i18n dictionary in the app (blog meta, generator labels,
// embed-comment phrasing, widget-keyword templates, widget FAQ
// templates, etc.). Landing.jsx imports `homeT` from here, so the
// monolith landed in main.js for every visitor — even those who never
// click into a blog, widget landing, or the Generator. Split into:
//
//   - content_i18n.js          (this file — kept tiny, in main bundle)
//   - content_i18n_blog.js     (BLOG_META_I18N + blogMetaForLang)
//   - content_i18n_gen.js      (GEN_I18N + genT + embedCommentForLang)
//   - content_i18n_widget.js   (widgetMetaForLang + keywordsForLang + widgetFaqForLang)
//
// Net main.js reduction: ~25 KB raw / ~6-8 KB gzipped.

// ─── HOMEPAGE EXTENDED STRINGS (widget cards, marquee headers, footer, SEO) ───
// HOME_I18N now backed by a split-bundle architecture: EN is shipped
// eagerly in `home_i18n_en.js` and all other locales lazy-load via
// `home_i18n_other.js` (see useI18n in `lib/i18n.js`).
import { HOME_I18N_EN } from "./home_i18n_en";
export const HOME_I18N = { en: HOME_I18N_EN };

// Module-level hook used by the i18n provider to merge the lazy locale
// chunk into HOME_I18N. Idempotent — re-calls are no-ops.
let _otherLoaded = false;
export const _hydrateHomeI18nOther = (others) => {
  if (_otherLoaded) return;
  Object.assign(HOME_I18N, others);
  _otherLoaded = true;
};

// AI-translated content (blog + widget landings) — generated by
// frontend/scripts/translate_content.js. Imported via the new lazy-load
// API in blog_widget_translations.js so the per-locale JSON dicts
// (~330 KB raw / ~120 KB gzipped combined) no longer ship in main.js.
import { tWidget as _aiTWidget, tTool as _aiTTool } from "./blog_widget_translations";

// Convenience helpers. `faqsForLang` and `FAQS_I18N` live in
// `./faqs_i18n.js` so they can be lazy-loaded by Landing's <FAQ />
// component (saves ~73 KB from the main bundle = ~700 ms LCP on 4G).
export const homeT = (lang, key) =>
  (HOME_I18N[lang] && HOME_I18N[lang][key]) || HOME_I18N.en[key] || key;

/**
 * Compact helper for surfaces that ONLY need the localized display name
 * (gallery cards, cross-link footers, blog sidebars). Falls back to the
 * English `enDisplayName` when the locale dict is missing the entry.
 */
export const widgetDisplayNameForLang = (slug, lang, enDisplayName) => {
  if (!lang || lang === "en") return enDisplayName;
  return _aiTWidget(lang, slug, "displayName", enDisplayName);
};

/**
 * Localized SEO-tool short title — used by Landing's nav menubar dropdown
 * and SeoToolsIndex card titles. Falls back to English when missing.
 */
export const seoToolTitleForLang = (slug, lang, enTitle) => {
  if (!lang || lang === "en") return enTitle;
  return _aiTTool(lang, slug, "titleShort", enTitle);
};

/**
 * Returns the localized SEO-tool meta bundle (`titleLong`, `h1`,
 * `metaDescription`, `intro`, `titleShort`) for Helmet tags + page hero.
 *
 * Imported eagerly by `lib/i18n.js`'s `seoMetaFor()` adapter, so this
 * stays in the main bundle. (The underlying per-locale JSON is still
 * lazy — see `blog_widget_translations.js`.)
 */
export const seoToolMetaForLang = (slug, lang, fallbacks = {}) => {
  if (!lang || lang === "en") return fallbacks;
  return {
    titleLong: _aiTTool(lang, slug, "titleLong", fallbacks.titleLong || ""),
    h1: _aiTTool(lang, slug, "h1", fallbacks.h1 || ""),
    metaDescription: _aiTTool(lang, slug, "metaDescription", fallbacks.metaDescription || ""),
    intro: _aiTTool(lang, slug, "intro", fallbacks.intro || ""),
    titleShort: _aiTTool(lang, slug, "titleShort", fallbacks.titleShort || ""),
  };
};
